import React from 'react';
import { logger } from 'src/utils';

export interface IResolvedImage {
  src: string | null;
  failed: boolean;
}

// Keyed by attachment id so every atom resolving the same id (a tile and its
// preview, or two tiles for the same attachment) shares one in-flight
// resolve rather than each firing its own downloadFile call. A rejected
// promise is evicted so a retry (re-enabling, remount, etc.) re-attempts
// rather than replaying the same failure forever.
const urlCache = new Map<string, Promise<string>>();

/** Test-only: clears the shared resolve cache so tests reusing an id across cases don't observe a prior test's promise. */
export const resetResolvedImageUrlCache = (): void => {
  urlCache.clear();
};

/** @registry Resolves an attachment id to a displayable image url via the caller's resolveImageUrl; the single place image-URL resolution and its failure handling live for both ImageThumbnail and ImageReviewCard's preview. */
export const useResolvedImageUrl = (
  id: string,
  resolveImageUrl: (id: string) => Promise<string>,
  enabled = true,
): IResolvedImage => {
  const [src, setSrc] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  // Ref-stabilized so an inline resolveImageUrl arrow (a new identity every
  // render) doesn't appear in the effect's deps and re-trigger it.
  const resolveRef = React.useRef(resolveImageUrl);
  resolveRef.current = resolveImageUrl;

  React.useEffect(() => {
    setSrc(null);
    setFailed(false);
    if (!enabled) return undefined;

    let active = true;
    let cached = urlCache.get(id);
    if (!cached) {
      cached = resolveRef.current(id);
      urlCache.set(id, cached);
    }
    cached
      .then((url) => {
        if (active) setSrc(url);
      })
      .catch((err) => {
        urlCache.delete(id);
        if (active) {
          logger.error('Failed to resolve image url', { err, id });
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [id, enabled]);

  return { src, failed };
};
